import { createContext, useContext, useState, useCallback } from "react";
import AiChatApi from "Api/AiChatApi";

export const AiChatContext = createContext({});

/**
 * Holds the AI assistant conversation. State lives here (not in the popup) so
 * the chat survives route changes while the popup stays mounted.
 *
 * Message shape: { role: 'user' | 'assistant', content, usage?, context?, tool_calls?, error? }
 */
export const AiChatProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((v) => !v), []);

    // Resets the server-side conversation as well as the local display state.
    const clear = useCallback(async () => {
        try {
            await AiChatApi.reset();
        } catch (e) {
            // Best effort: even if the reset call fails, clear the local view.
        }
        setMessages([]);
        setSending(false);
    }, []);

    // Sends a single new user message; the backend holds the full history, so we
    // only append the assistant reply (or an error bubble on failure) locally.
    const runChat = useCallback(async (text) => {
        setSending(true);
        try {
            const res = await AiChatApi.send(text);

            if (res.status >= 200 && res.status < 300 && res.json?.message) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: res.json.message.content,
                        usage: res.json.usage,
                        context: res.json.context,
                        tool_calls: res.json.tool_calls,
                    },
                ]);
            } else {
                throw new Error(res.json?.error || "Falha na resposta do assistente.");
            }
        } catch (e) {
            const message = e?.response?.data?.error || e?.message || "Não foi possível obter uma resposta.";
            setMessages((prev) => [...prev, { role: "assistant", error: true, content: message }]);
        } finally {
            setSending(false);
        }
    }, []);

    const sendMessage = useCallback(
        (text) => {
            const content = (text || "").trim();
            if (!content || sending) return;

            setMessages((prev) => [...prev, { role: "user", content }]);
            runChat(content);
        },
        [sending, runChat]
    );

    // Drops the trailing error bubble and re-sends the last user message. The
    // backend never persisted the failed turn, so re-sending the text is correct.
    const retryLast = useCallback(() => {
        if (sending) return;

        const trimmed = messages[messages.length - 1]?.error ? messages.slice(0, -1) : messages;
        const lastUser = [...trimmed].reverse().find((m) => m.role === "user");
        if (!lastUser) return;

        setMessages(trimmed);
        runChat(lastUser.content);
    }, [messages, sending, runChat]);

    return (
        <AiChatContext.Provider
            value={{ isOpen, messages, sending, open, close, toggle, sendMessage, retryLast, clear }}
        >
            {children}
        </AiChatContext.Provider>
    );
};

export const useAiChat = () => useContext(AiChatContext);
